import React from "react"
import {
  AlertTriangle,
  BrainCircuit,
  CheckCircle2,
  Circle,
  Download,
  Flag,
  RotateCcw,
} from "lucide-react"
import type { Attempt } from "../types"
import { downloadQuestionResource } from "../utils/printQuestion"
import "./AIFeedbackPanel.style.scss"

interface AIFeedbackPanelProps {
  attempt: Attempt
  canStartNew: boolean
  onNewAttempt: () => void
}

const AIFeedbackPanel = ({
  attempt,
  canStartNew,
  onNewAttempt,
}: AIFeedbackPanelProps) => {
  const {
    score,
    maxScore,
    criteriaBreakdown,
    strengths,
    improvements,
    suggestedAnswer,
    examinerFeedback,
    confidence,
    status,
  } = attempt

  const pct =
    maxScore && maxScore > 0 ? Math.round(((score ?? 0) / maxScore) * 100) : 0

  const scoreTone =
    pct === 100 ? "is-full" : pct >= 50 ? "is-mid" : "is-low"

  const handleDownload = () => {
    const html = `
      <h2>AI Assessor Feedback</h2>
      <p><strong>Score:</strong> ${score ?? 0}/${maxScore ?? 0} (${pct}%)</p>
      ${
        criteriaBreakdown
          ?.map(
            (c) =>
              `<p><strong>${c.label}</strong> — ${c.marksAwarded}/${c.marksAvailable}<br/>${c.rationale}</p>`
          )
          .join("") || ""
      }
      ${examinerFeedback ? `<p><em>${examinerFeedback}</em></p>` : ""}
      ${suggestedAnswer ? `<h3>Model Answer</h3><p>${suggestedAnswer}</p>` : ""}
    `
    downloadQuestionResource({
      filename: `attempt-${attempt.attemptNumber}-feedback`,
      title: `Attempt ${attempt.attemptNumber} — AI Feedback`,
      content: html,
    })
  }

  return (
    <div className="ai-feedback">
      <div className="ai-feedback__card">
        <div className="ai-feedback__header">
          <div className="ai-feedback__title">
            <span className="ai-feedback__avatar">
              <BrainCircuit size={18} />
            </span>
            <div>
              <h4>AI Assessor Feedback</h4>
              {status === "flagged_for_review" && (
                <span className="ai-feedback__badge is-flagged">
                  <Flag size={12} /> Score is provisional — flagged for manual review
                </span>
              )}
              {confidence === "high" && status === "marked" && (
                <span className="ai-feedback__badge is-high">
                  <CheckCircle2 size={12} /> High confidence marking
                </span>
              )}
              {confidence === "medium" && status === "marked" && (
                <span className="ai-feedback__badge is-high">
                  <CheckCircle2 size={12} /> Medium confidence marking
                </span>
              )}
            </div>
          </div>
          <div className="ai-feedback__score-block">
            <span className={`ai-feedback__score ${scoreTone}`}>
              {score}/{maxScore}
            </span>
            <p>{pct}%</p>
          </div>
        </div>

        <div className="ai-feedback__bar">
          <div
            className={scoreTone}
            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
          />
        </div>

        {criteriaBreakdown && criteriaBreakdown.length > 0 && (
          <div className="ai-feedback__criteria">
            {criteriaBreakdown.map((c) => (
              <div key={c.label} className="ai-feedback__criterion">
                <div className="ai-feedback__criterion-top">
                  <div>
                    {c.marksAwarded > 0 ? (
                      <CheckCircle2 size={16} className="is-pass" />
                    ) : (
                      <Circle size={16} className="is-miss" />
                    )}
                    <strong>{c.label}</strong>
                  </div>
                  <span className={c.marksAwarded > 0 ? "is-pass" : "is-miss"}>
                    {c.marksAwarded}/{c.marksAvailable} mark
                  </span>
                </div>
                <p>{c.rationale}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {((strengths && strengths.length > 0) ||
        (improvements && improvements.length > 0)) && (
        <div className="ai-feedback__split">
          {strengths && strengths.length > 0 && (
            <div className="ai-feedback__panel is-strength">
              <h5>Strengths</h5>
              <ul>
                {strengths.map((s) => (
                  <li key={s}>
                    <CheckCircle2 size={14} /> {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {improvements && improvements.length > 0 && (
            <div className="ai-feedback__panel is-improve">
              <h5>Areas to Improve</h5>
              <ul>
                {improvements.map((s) => (
                  <li key={s}>
                    <AlertTriangle size={14} /> {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {suggestedAnswer && (
        <div className="ai-feedback__panel is-model">
          <h5>Model Answer</h5>
          <p className="is-serif">{suggestedAnswer}</p>
        </div>
      )}

      {examinerFeedback && (
        <div className="ai-feedback__panel is-note">
          <h5>Examiner Note</h5>
          <p className="is-italic">“{examinerFeedback}”</p>
        </div>
      )}

      <div className="ai-feedback__actions">
        <button type="button" className="is-ghost" onClick={handleDownload}>
          <Download size={16} /> Download PDF
        </button>
        {canStartNew && (
          <button type="button" className="is-primary" onClick={onNewAttempt}>
            <RotateCcw size={16} /> Retake Question
          </button>
        )}
      </div>
    </div>
  )
}

export default AIFeedbackPanel
